import { onCLS, onFCP, onLCP, onTTFB, onINP } from 'web-vitals';
import type { Metric } from 'web-vitals';
import * as Sentry from '@sentry/react';
import { Config, ENV } from '@/config/environment';

// Metric types
export type MetricName = 'CLS' | 'FCP' | 'LCP' | 'TTFB' | 'INP';
export type MetricRating = 'good' | 'needs-improvement' | 'poor';

// Web Vitals metric with extended info
export interface WebVitalsMetric {
  name: MetricName;
  value: number;
  rating: MetricRating;
  delta: number;
  id: string;
  navigationType: string;
  timestamp: number;
}

// Thresholds based on Google's recommendations
export const DEFAULT_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 }, // Largest Contentful Paint
  FCP: { good: 1800, needsImprovement: 3000 }, // First Contentful Paint
  CLS: { good: 0.1, needsImprovement: 0.25 },  // Cumulative Layout Shift
  TTFB: { good: 800, needsImprovement: 1800 }, // Time to First Byte
  INP: { good: 200, needsImprovement: 500 },   // Interaction to Next Paint (replaced FID)
};

// Configuration interface
export interface WebVitalsConfig {
  reportToConsole: boolean;
  reportToAnalytics: boolean;
  reportToSentry: boolean;
  thresholds: typeof DEFAULT_THRESHOLDS;
  onMetric?: (metric: WebVitalsMetric) => void;
}

// Default configuration
const DEFAULT_CONFIG: WebVitalsConfig = {
  reportToConsole: ENV.isDev,
  reportToAnalytics: ENV.isProd,
  reportToSentry: ENV.isProd && Config.features.enableSentry,
  thresholds: DEFAULT_THRESHOLDS,
};

// Store for metric callbacks
const metricCallbacks: Set<(metric: WebVitalsMetric) => void> = new Set();

// Get rating based on thresholds
function getMetricRating(
  name: MetricName,
  value: number,
  thresholds: typeof DEFAULT_THRESHOLDS
): MetricRating {
  const threshold = thresholds[name];
  if (!threshold) return 'poor';

  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
}

// Convert web-vitals Metric to our WebVitalsMetric
function convertMetric(
  metric: Metric,
  thresholds: typeof DEFAULT_THRESHOLDS
): WebVitalsMetric {
  return {
    name: metric.name as MetricName,
    value: metric.value,
    rating: getMetricRating(metric.name as MetricName, metric.value, thresholds),
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType || 'unknown',
    timestamp: Date.now(),
  };
}

// Report metric to console
function reportToConsole(metric: WebVitalsMetric) {
  const emoji = metric.rating === 'good' ? '✅' : metric.rating === 'needs-improvement' ? '⚠️' : '❌';
  const color = metric.rating === 'good' ? 'green' : metric.rating === 'needs-improvement' ? 'orange' : 'red';
  
  console.log(
    `%c${emoji} ${metric.name}: ${metric.value.toFixed(2)}ms (${metric.rating})`,
    `color: ${color}; font-weight: bold;`,
    {
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
    }
  );
}

// Report metric to Sentry
function reportToSentry(metric: WebVitalsMetric) {
  if (!Config.features.enableSentry) return;

  // Set context for web vitals
  Sentry.setContext('web_vitals', {
    metric: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    navigationType: metric.navigationType,
  });

  // Send as custom event for tracking
  Sentry.captureEvent({
    message: `Web Vitals: ${metric.name}`,
    level: metric.rating === 'poor' ? 'warning' : 'info',
    tags: {
      'web_vitals.metric': metric.name,
      'web_vitals.rating': metric.rating,
      'web_vitals.navigation_type': metric.navigationType,
    },
    extra: {
      value: metric.value,
      delta: metric.delta,
      id: metric.id,
      threshold: DEFAULT_THRESHOLDS[metric.name],
    },
    measurements: {
      [`web_vitals.${metric.name.toLowerCase()}`]: {
        value: metric.value,
        unit: metric.name === 'CLS' ? 'none' : 'millisecond',
      },
    },
  });
}

// Report metric to analytics (placeholder for future implementation)
function reportToAnalytics(metric: WebVitalsMetric) {
  // This is where you would send to Google Analytics, Mixpanel, etc.
  // For now, we'll just log it
  if (ENV.isProd) {
    console.info('Analytics:', metric);
  }
}

// Handle metric reporting
function handleMetric(metric: Metric, config: WebVitalsConfig) {
  const webVitalsMetric = convertMetric(metric, config.thresholds);

  // Report to various destinations
  if (config.reportToConsole) {
    reportToConsole(webVitalsMetric);
  }

  if (config.reportToSentry) {
    reportToSentry(webVitalsMetric);
  }

  if (config.reportToAnalytics) {
    reportToAnalytics(webVitalsMetric);
  }

  // Call custom handler if provided
  if (config.onMetric) {
    config.onMetric(webVitalsMetric);
  }

  // Notify all registered callbacks
  metricCallbacks.forEach(callback => callback(webVitalsMetric));
}

// Initialize web vitals monitoring
export function initWebVitals(customConfig?: Partial<WebVitalsConfig>) {
  const config = { ...DEFAULT_CONFIG, ...customConfig };

  // Set up metric collection
  onCLS((metric) => handleMetric(metric, config));
  onFCP((metric) => handleMetric(metric, config));
  onLCP((metric) => handleMetric(metric, config));
  onTTFB((metric) => handleMetric(metric, config));
  onINP((metric) => handleMetric(metric, config));

  if (ENV.isDev) {
    console.log('📊 Web Vitals monitoring initialized');
  }
}

// Subscribe to metric updates
export function subscribeToMetrics(callback: (metric: WebVitalsMetric) => void) {
  metricCallbacks.add(callback);
  
  // Return unsubscribe function
  return () => {
    metricCallbacks.delete(callback);
  };
}

// Get current thresholds
export function getThresholds() {
  return DEFAULT_THRESHOLDS;
}

// Check if a metric value is within good range
export function isGoodMetric(name: MetricName, value: number): boolean {
  return value <= DEFAULT_THRESHOLDS[name].good;
}

// Get all metric names
export function getMetricNames(): MetricName[] {
  return ['CLS', 'FCP', 'LCP', 'TTFB', 'INP'];
}